/**
 * FK Selection Modal
 *
 * Modal for selecting foreign key references when creating/editing records.
 * Supports Magic Bridge resolution for complex join structures.
 */

import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  FormControl,
  FormLabel,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/joy";
import { useQuery } from "@tanstack/react-query";
import { useAuthFetch } from "../../../../utils/useAuthFetch";
import { useWorkspaceContext } from "../../../../providers/WorkspaceProvider";
import {
  useViewJoinTree,
  useResolveBridge,
  hasMagicBridges,
  isBridgeResolvable,
  getBridgeSubSelections,
} from "../../hooks/useMagicBridge";
import CascadingFkPicker, {
  BridgeDisambiguationPicker,
} from "./CascadingFkPicker";
import type {
  CascadingSelection,
  ExtendedJoinedTableInfo,
  ViewJoinTreeResponse,
  BridgeOption,
} from "../../types/magicBridge";

/**
 * FK prefill value returned when user confirms selection
 */
export interface FkPrefillValue {
  fkValue: string;
  displayValue: string;
  fieldValues?: Record<string, string | null>;
}

/**
 * Information about a joined table that needs FK selection
 */
export interface JoinedTableInfo {
  alias: string;
  tableName: string;
  joinId?: string;
  fieldId?: string;
  displayLabel: string;
  isRequired: boolean;
  fkColumnKey?: string;
  fkColumnName?: string;
  hasLinkField: boolean;
}

interface ReferenceOption {
  id: string;
  displayValue: string;
  secondaryInfo?: string;
}

interface ReferenceOptionsResponse {
  options: ReferenceOption[];
  total: number;
  hasMore: boolean;
  fkColumnName?: string;
}

interface FkSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (fkValues: Record<string, FkPrefillValue>) => void;
  viewId: string;
  joinedTables: JoinedTableInfo[];
  title?: string;
  confirmText?: string;
}

/**
 * Hook to fetch reference options for a field or join
 */
function useReferenceOptions(
  authFetch: ReturnType<typeof useAuthFetch>,
  workspaceId: string,
  viewId: string,
  tableInfo: JoinedTableInfo,
  search: string,
  enabled: boolean,
) {
  const endpoint =
    tableInfo.hasLinkField && tableInfo.fieldId
      ? `/api/workspace/myvault/views/${viewId}/fields/${tableInfo.fieldId}/reference-options`
      : `/api/workspace/myvault/views/${viewId}/joins/${tableInfo.joinId}/reference-options`;

  return useQuery<ReferenceOptionsResponse>({
    queryKey: [
      "reference-options",
      workspaceId,
      viewId,
      tableInfo.fieldId || tableInfo.joinId,
      search,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        search,
        page: "1",
        page_size: "50",
      });
      const response = await authFetch(
        `${endpoint}?${params}`,
        "fetch reference options",
        {
          headers: { "x-workspace-id": workspaceId },
        },
      );
      return response;
    },
    enabled: enabled && !!(tableInfo.fieldId || tableInfo.joinId),
    staleTime: 30000,
  });
}

/**
 * Simple FK picker component (for non-magic-bridge cases)
 */
function SimpleFkPicker({
  viewId,
  tableInfo,
  value,
  onChange,
  onFkColumnNameResolved,
}: {
  viewId: string;
  tableInfo: JoinedTableInfo;
  value: ReferenceOption | null;
  onChange: (option: ReferenceOption | null) => void;
  onFkColumnNameResolved?: (alias: string, fkColumnName: string) => void;
}) {
  const authFetch = useAuthFetch();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  const [inputValue, setInputValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(inputValue);
    }, 300);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const { data, isLoading } = useReferenceOptions(
    authFetch,
    workspaceId,
    viewId,
    tableInfo,
    debouncedSearch,
    true,
  );

  useEffect(() => {
    if (data?.fkColumnName && onFkColumnNameResolved) {
      onFkColumnNameResolved(tableInfo.alias, data.fkColumnName);
    }
  }, [data?.fkColumnName, tableInfo.alias, onFkColumnNameResolved]);

  const options = data?.options || [];
  const allOptions: ReferenceOption[] = tableInfo.isRequired
    ? options
    : [{ id: "", displayValue: "(None)" }, ...options];

  return (
    <FormControl sx={{ mb: 2 }}>
      <FormLabel required={tableInfo.isRequired}>
        {tableInfo.displayLabel}
      </FormLabel>
      <Autocomplete
        value={value}
        onChange={(_, newValue) => {
          if (newValue?.id === "") {
            onChange(null);
          } else {
            onChange(newValue);
          }
        }}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue);
        }}
        options={allOptions}
        getOptionLabel={(option) => option.displayValue}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        loading={isLoading}
        placeholder={`Select ${tableInfo.displayLabel}...`}
        slotProps={{
          input: {
            endDecorator: isLoading ? <CircularProgress size="sm" /> : null,
          },
        }}
      />
    </FormControl>
  );
}

/**
 * Build ExtendedJoinedTableInfo from basic JoinedTableInfo and join tree
 */
function buildExtendedJoinInfo(
  tables: JoinedTableInfo[],
  joinTree: ViewJoinTreeResponse | undefined,
): ExtendedJoinedTableInfo[] {
  if (!joinTree) {
    // No join tree - just convert basic info without dependencies
    return tables.map((t) => ({
      ...t,
      parentJoinId: null,
      childJoinIds: [],
      dependsOnAliases: [],
    }));
  }

  // Build dependency map from magic bridges
  const aliasDependencies: Record<string, string[]> = {};

  for (const bridge of joinTree.magicBridges || []) {
    // Resolution joins might depend on each other
    const resolutionAliases = bridge.resolutionJoins.map((rj) => rj.tableAlias);

    // Build cascading dependencies - each picker might depend on previous ones
    for (let i = 0; i < resolutionAliases.length; i++) {
      const alias = resolutionAliases[i];
      // Depends on all previous resolution joins in this bridge
      aliasDependencies[alias] = resolutionAliases.slice(0, i);
    }
  }

  return tables.map((t) => ({
    ...t,
    parentJoinId: null,
    childJoinIds: [],
    dependsOnAliases: aliasDependencies[t.alias] || [],
    isMagicBridge: joinTree.magicBridges?.some(
      (b) => b.bridgeJoin.tableAlias === t.alias,
    ),
  }));
}

export default function FkSelectionModal({
  open,
  onClose,
  onConfirm,
  viewId,
  joinedTables,
  title = "Select Related Records",
  confirmText = "Confirm",
}: FkSelectionModalProps) {
  // Track selections by alias (for both simple and cascading)
  const [selections, setSelections] = useState<
    Record<string, ReferenceOption | null>
  >({});
  const [cascadingSelections, setCascadingSelections] = useState<
    Record<string, CascadingSelection>
  >({});
  // Track resolved FK column names
  const [resolvedFkColumnNames, setResolvedFkColumnNames] = useState<
    Record<string, string>
  >({});
  // Track resolved bridge IDs
  const [resolvedBridges, setResolvedBridges] = useState<
    Record<string, string>
  >({});
  // Disambiguation state
  const [disambiguation, setDisambiguation] = useState<{
    bridgeJoinId: string;
    options: BridgeOption[];
    displayColumns: string[];
  } | null>(null);

  // Fetch join tree to determine if we have magic bridges
  const { data: joinTree, isLoading: isLoadingJoinTree } = useViewJoinTree(
    viewId,
    open,
  );
  const hasBridges = hasMagicBridges(joinTree);

  // Bridge resolution mutation
  const resolveBridgeMutation = useResolveBridge();

  // Build extended join info with dependencies
  const extendedJoinInfo = useMemo(
    () => buildExtendedJoinInfo(joinedTables, joinTree),
    [joinedTables, joinTree],
  );

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSelections({});
      setCascadingSelections({});
      setResolvedFkColumnNames({});
      setResolvedBridges({});
      setDisambiguation(null);
    }
  }, [open]);

  // Handle FK column name resolution
  const handleFkColumnNameResolved = useCallback(
    (alias: string, fkColumnName: string) => {
      setResolvedFkColumnNames((prev) => ({
        ...prev,
        [alias]: fkColumnName,
      }));
    },
    [],
  );

  // Handle simple selection change
  const handleSelectionChange = useCallback(
    (alias: string, option: ReferenceOption | null) => {
      setSelections((prev) => ({
        ...prev,
        [alias]: option,
      }));
    },
    [],
  );

  // Handle cascading selection change
  const handleCascadingChange = useCallback(
    (alias: string, selection: CascadingSelection | null) => {
      if (selection) {
        setCascadingSelections((prev) => ({
          ...prev,
          [alias]: selection,
        }));
      } else {
        setCascadingSelections((prev) => {
          const next = { ...prev };
          delete next[alias];
          return next;
        });
      }
    },
    [],
  );

  // Try to resolve bridges when cascading selections change
  useEffect(() => {
    if (!joinTree?.magicBridges) return;

    for (const bridge of joinTree.magicBridges) {
      const bridgeJoinId = bridge.bridgeJoin.joinId;

      // Skip if already resolved
      if (resolvedBridges[bridgeJoinId]) continue;

      // Check if all resolution joins are selected
      if (isBridgeResolvable(joinTree, bridgeJoinId, cascadingSelections)) {
        const subSelections = getBridgeSubSelections(
          joinTree,
          bridgeJoinId,
          cascadingSelections,
        );

        // Attempt to resolve
        resolveBridgeMutation.mutate(
          { viewId, bridgeJoinId, subJoinSelections: subSelections },
          {
            onSuccess: (result) => {
              if (result.matchCount === 1 && result.resolvedId) {
                // Single match - auto-resolve
                setResolvedBridges((prev) => ({
                  ...prev,
                  [bridgeJoinId]: result.resolvedId!,
                }));
                if (result.fkColumnName) {
                  setResolvedFkColumnNames((prev) => ({
                    ...prev,
                    [bridge.bridgeJoin.tableAlias]: result.fkColumnName!,
                  }));
                }
              } else if (result.matchCount > 1) {
                // Multiple matches - need disambiguation
                setDisambiguation({
                  bridgeJoinId,
                  options: result.options,
                  displayColumns: result.displayColumns,
                });
              }
              // Zero matches - no valid record, user will see error
            },
          },
        );
      }
    }
  }, [
    cascadingSelections,
    joinTree,
    viewId,
    resolveBridgeMutation,
    resolvedBridges,
  ]);

  // Handle disambiguation selection
  const handleDisambiguationSelect = useCallback(
    (id: string | null) => {
      if (id && disambiguation) {
        setResolvedBridges((prev) => ({
          ...prev,
          [disambiguation.bridgeJoinId]: id,
        }));
        setDisambiguation(null);
      }
    },
    [disambiguation],
  );

  // Check if all required fields are filled
  const allRequiredFilled = useMemo(() => {
    if (hasBridges) {
      // For magic bridges, check cascading selections and bridge resolution
      return extendedJoinInfo.every((info) => {
        if (!info.isRequired) return true;
        if (info.isMagicBridge) {
          // Bridge must be resolved
          const bridge = joinTree?.magicBridges?.find(
            (b) => b.bridgeJoin.tableAlias === info.alias,
          );
          return bridge ? !!resolvedBridges[bridge.bridgeJoin.joinId] : true;
        }
        // Regular cascading selection
        return !!cascadingSelections[info.alias]?.selectedId;
      });
    }

    // Simple mode - check basic selections
    return joinedTables.every((table) => {
      if (!table.isRequired) return true;
      return selections[table.alias]?.id;
    });
  }, [
    hasBridges,
    extendedJoinInfo,
    cascadingSelections,
    joinTree,
    resolvedBridges,
    joinedTables,
    selections,
  ]);

  // Build final FK values on confirm
  const handleConfirm = useCallback(() => {
    const fkValues: Record<string, FkPrefillValue> = {};

    if (hasBridges) {
      // Magic bridge mode - use cascading selections
      for (const info of extendedJoinInfo) {
        if (info.isMagicBridge) {
          // Get resolved bridge ID
          const bridge = joinTree?.magicBridges?.find(
            (b) => b.bridgeJoin.tableAlias === info.alias,
          );
          if (bridge && resolvedBridges[bridge.bridgeJoin.joinId]) {
            const key =
              info.fkColumnKey ||
              resolvedFkColumnNames[info.alias] ||
              bridge.targetFkColumn ||
              info.alias;
            fkValues[key] = {
              fkValue: resolvedBridges[bridge.bridgeJoin.joinId],
              displayValue: `${info.displayLabel} (resolved)`,
            };
          }
        } else {
          // Regular cascading selection
          const selection = cascadingSelections[info.alias];
          if (selection?.selectedId) {
            const key =
              info.fkColumnKey ||
              resolvedFkColumnNames[info.alias] ||
              info.alias;
            fkValues[key] = {
              fkValue: selection.selectedId,
              displayValue: selection.selectedDisplayValue,
              fieldValues: selection.fieldValues,
            };
          }
        }
      }
    } else {
      // Simple mode
      for (const table of joinedTables) {
        const selection = selections[table.alias];
        if (selection?.id) {
          const key =
            table.fkColumnKey ||
            resolvedFkColumnNames[table.alias] ||
            table.alias;
          fkValues[key] = {
            fkValue: selection.id,
            displayValue: selection.displayValue,
          };
        }
      }
    }

    onConfirm(fkValues);
  }, [
    hasBridges,
    extendedJoinInfo,
    joinTree,
    resolvedBridges,
    cascadingSelections,
    resolvedFkColumnNames,
    joinedTables,
    selections,
    onConfirm,
  ]);

  // Build parent selections map for cascading pickers
  const parentSelectionsMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const [alias, selection] of Object.entries(cascadingSelections)) {
      if (selection?.selectedId) {
        map[alias] = selection.selectedId;
      }
    }
    return map;
  }, [cascadingSelections]);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ minWidth: 450, maxWidth: 600 }}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Typography level="body-sm" sx={{ mb: 3, color: "text.secondary" }}>
            Select the records to link. Required fields must be filled before
            continuing.
          </Typography>

          {isLoadingJoinTree ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : hasBridges ? (
            // Magic Bridge mode - cascading pickers
            <>
              {disambiguation && (
                <Alert color="warning" sx={{ mb: 2 }}>
                  <BridgeDisambiguationPicker
                    options={disambiguation.options}
                    displayColumns={disambiguation.displayColumns}
                    value={null}
                    onChange={handleDisambiguationSelect}
                    label="Select Record"
                    size="sm"
                  />
                </Alert>
              )}

              {extendedJoinInfo
                .filter((info) => !info.isMagicBridge) // Hide bridge tables
                .map((info) => (
                  <CascadingFkPicker
                    key={info.alias}
                    viewId={viewId}
                    joinInfo={info}
                    value={cascadingSelections[info.alias] || null}
                    onChange={(sel) => handleCascadingChange(info.alias, sel)}
                    parentSelections={parentSelectionsMap}
                    onFkColumnNameResolved={handleFkColumnNameResolved}
                    size="sm"
                  />
                ))}

              {/* Show resolved bridges */}
              {joinTree?.magicBridges?.map((bridge) => {
                const resolvedId = resolvedBridges[bridge.bridgeJoin.joinId];
                if (resolvedId) {
                  return (
                    <Box key={bridge.bridgeJoin.joinId} sx={{ mb: 2 }}>
                      <Divider sx={{ my: 1 }} />
                      <Typography level="body-xs" sx={{ color: "success.500" }}>
                        Bridge resolved: {bridge.bridgeJoin.tableName}
                      </Typography>
                    </Box>
                  );
                }
                return null;
              })}
            </>
          ) : (
            // Simple mode - basic pickers
            joinedTables.map((table) => (
              <SimpleFkPicker
                key={table.alias}
                viewId={viewId}
                tableInfo={table}
                value={selections[table.alias] || null}
                onChange={(opt) => handleSelectionChange(table.alias, opt)}
                onFkColumnNameResolved={handleFkColumnNameResolved}
              />
            ))
          )}

          {resolveBridgeMutation.isError && (
            <Alert color="danger" sx={{ mt: 2 }}>
              Failed to resolve bridge record. Please try again.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="solid"
            color="primary"
            onClick={handleConfirm}
            disabled={!allRequiredFilled || resolveBridgeMutation.isPending}
            loading={resolveBridgeMutation.isPending}
          >
            {confirmText}
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}
